import { DateTime } from "luxon"
import { Box } from "@mui/system"
import { add, dinero } from "dinero.js"
import { useEffect, useMemo, useState } from "react"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRotateRight } from "@fortawesome/pro-light-svg-icons"
import { IconButton, MenuItem, Tooltip, Typography } from "@mui/material"
import { MRT_Row } from "material-react-table"
import { DropdownExportButtonPurchases } from "../../../components/DropdownExportButtonPurchases"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDineroOrUndefined } from "../../../../currency/currencyLib"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Receipt, ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { selectEffectiveCompanyId } from "../../../../context/contextSelectors"
import { MoneyFactory, useMoney } from "../../../../currency/useMoney"
import { selectUserGroupId } from "../../../../login/loginSelectors"
import { fetchPurchasesByPeriod, TableOrder } from "../purchasesApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../store/storeHooks"
import type { MRT_ColumnDef } from "material-react-table"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"

export const OrdersContent = () => {
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const userGroupId = useAppSelector(selectUserGroupId)
    const moneyFactory: MoneyFactory = useMoney({})

    const { l10n } = useAppLocalization()

    // const [startDate, setStartDate] = useState<DateTime | null>(DateTime.now().minus({ days: 14 }))
    const [chosenStartDate, setChosenStartDate] = useState<string>(DateTime.now().toISODate())

    // const [endDate, setEndDate] = useState<DateTime | null>(DateTime.now())
    const [chosenEndDate, setChosenEndDate] = useState<string>(DateTime.now().toISODate())

    const { startDate, endDate } = useInitialDateFilters()
    const [dateRange, setDateRange] = useState(() => ({
        startDate: startDate ?? DateTime.now().minus({ days: 14 }),
        endDate: endDate ?? DateTime.now(),
    }))

    const [receipt, setReceipt] = useState<Receipt>()

    const getTotalFilteredRowAmount = (data: MRT_Row<TableOrder>[]) => {
        const totalTransactions = data.length

        const currency = toCurrency("DKK") // TODO: Take from input data
        const zero = dinero({ amount: 0, scale: 2, currency })
        const total = data.reduce((acc, row) => {
            const rowValue = toDineroOrUndefined(row?.original?.amount) ?? zero
            return add(acc, rowValue)
        }, zero)

        return (
            <>
                <LocalizedStrict id="office-period-total" vars={{ periodTotal: moneyFactory.format(total) }}>
                    <p>{"Total for period: " + moneyFactory.format(total)}</p>
                </LocalizedStrict>
                <LocalizedStrict
                    id="office-transation-period-total"
                    vars={{ transactionPeriodTotal: totalTransactions }}
                >
                    <p>{"Total transations for period: " + totalTransactions}</p>
                </LocalizedStrict>
            </>
        )
    }

    function showReceipt(row: TableOrder) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const lastUpdatedTime = () => {
        const timeInMS = queryResult.dataUpdatedAt
        const dateTime = DateTime.fromMillis(timeInMS)
        return dateTime.toLocaleString(DateTime.TIME_WITH_SECONDS)
    }

    const queryResult = useApiQuery(fetchPurchasesByPeriod, {
        queryName: "purchases-by-period",
        dependencies: {
            companyId: companyId ?? 0,
            userGroupId: userGroupId ?? 0,
            startDate: chosenStartDate,
            endDate: chosenEndDate,
        },
        select: (data) => {
            return data.orders.map((order) => ({
                ...order,
                startDate: data.reportDetails.startDate,
                endDate: data.reportDetails.endDate,
            }))
        },
        enabled: companyId !== undefined && userGroupId !== undefined && startDate !== null && endDate !== null,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(queryResult)

    const columns = useMemo<MRT_ColumnDef<TableOrder>[]>(
        () => [
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("office-purchase-time"),
                    accessorKey: "created",
                },
                (row) => row.created,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("office-purchase-name"),
                accessorKey: "customer.displayName",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("office-purchase-payment-details"),
                accessorKey: "paymentDetails.method",
                Cell: ({ cell }) => {
                    return (
                        <p style={{ inlineSize: "8rem", overflowWrap: "break-word" }}>
                            {cell.getValue() == undefined
                                ? ""
                                : l10n.getStringForEnum(
                                      "purchases-payment-method",
                                      cell.getValue() as string,
                                      cell.getValue() as string,
                                  )}
                        </p>
                    )
                },
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("office-purchase-amount"),
                    Footer: ({ table }) => (
                        <>{getTotalFilteredRowAmount(table.getFilteredRowModel().rows as MRT_Row<TableOrder>[])}</>
                    ),
                },
                (row) => row.amount,
                true,
            ),
        ],
        [l10n, columnFactory],
    )

    useEffect(() => {
        if (startDate !== null && endDate !== null) {
            setChosenStartDate(startDate.toISODate())
            setChosenEndDate(endDate.toISODate())
        }
    }, [])

    return (
        <>
            <ApiQueryTable
                columns={columns}
                enableRowSelection
                initialState={{ density: "spacious" }}
                positionActionsColumn="last"
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="office-purchase-receipt">
                        <MenuItem
                            onClick={() => {
                                showReceipt(row.original)
                                closeMenu()
                            }}
                            key={`purchase-receipt-${row.original.id}`}
                        >
                            Receipt
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <Box sx={{ whiteSpace: "nowrap" }}>
                            <LocalizedStrict id="office-last-updated" vars={{ lastUpdated: lastUpdatedTime() }}>
                                <Typography variant="caption" color="text.secondary">
                                    Last updated: {lastUpdatedTime()}
                                </Typography>
                            </LocalizedStrict>
                            <Tooltip title={l10n.getString("office-refresh")}>
                                <IconButton onClick={() => queryResult.refetch()}>
                                    <FontAwesomeIcon
                                        size="xs"
                                        icon={faArrowRotateRight}
                                        style={{ whiteSpace: "nowrap" }}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <DropdownExportButtonPurchases
                            rowsIsSelected={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                            containsRows={table.getPrePaginationRowModel().rows.length === 0}
                            allRowsWithFilter={table.getPrePaginationRowModel().rows}
                            seletecRows={table.getSelectedRowModel().rows}
                            chosenStartDate={chosenStartDate}
                            chosenEndDate={chosenEndDate}
                        />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter value={dateRange} onChange={setDateRange} />
                    </StandardTableFilter>
                )}
            />

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={queryResult.data}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}
        </>
    )
}
